import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography, Grid, CircularProgress, Card, CardMedia, Skeleton, useMediaQuery, useTheme } from '@mui/material';
import { Link } from 'react-router-dom';
import { getRecentPlayedGames } from '../../services/api';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import StarIcon from '@mui/icons-material/Star';
import RefreshIcon from '@mui/icons-material/Refresh';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import BlockIcon from '@mui/icons-material/Block';

interface RecentGame {
  game_id: number;
  name: string;
  japanese_name: string | null;
  cover_url: string | null;
  status: number;
  play_start_date: string | null;
  play_end_date: string | null;
  updated_at: string;
  priority_date: string;
  score: number | null;
  sub_status: number[];
  play_time: number | null;
}

interface RecentGamesWidgetProps {
  username?: string;
  isPublic?: boolean;
}

// カバー画像のURLを生成する関数（FavoriteGamesWidgetと同じ）
const getCoverUrl = (coverId: string | null) => {
  if (!coverId) return '/image/no_image.png';
  if (coverId.startsWith('pulse_')) {
    return `https://plsdb.com/cover/${coverId.replace('pulse_', '')}.jpg`;
  }
  return `https://images.igdb.com/igdb/image/upload/t_cover_big/${coverId}.jpg`;
};

// 共通のPaperスタイルを定義（FavoriteGamesWidgetと同じ）
const commonPaperStyle = {
  p: 2.5,
  height: '100%',
  borderRadius: 2,
  border: '1px solid rgba(255, 255, 255, 0.1)',
  background: 'background.paper',
  backdropFilter: 'blur(10px)',
  transition: 'all 0.2s ease',
  position: 'relative',
  mb: 0,
};

// GameCardと同じステータスアイコンを取得する関数
const getStatusIcon = (status: number, subStatus: number[]) => {
  // サブステータスに基づいて色を変更
  const isUnfinished = subStatus?.includes(8);
  const isRetired = subStatus?.includes(9);

  switch (status) {
    case 1: 
      if (isRetired) {
        return <BlockIcon fontSize="small" sx={{ color: 'error.main', opacity: 0.85 }} />;
      } else if (isUnfinished) {
        return <RemoveCircleOutlineIcon fontSize="small" sx={{ color: 'primary.main', opacity: 0.6 }} />;
      }
      return <CheckCircleOutlineIcon fontSize="small" sx={{ color: 'primary.main' }} />;
    case 2:
      if (isRetired) {
        return <PlayCircleOutlineIcon fontSize="small" sx={{ color: 'error.main' }} />;
      } else if (isUnfinished) {
        return <PlayCircleOutlineIcon fontSize="small" sx={{ color: 'info.main', opacity: 0.5 }} />;
      }
      return <PlayCircleOutlineIcon fontSize="small" sx={{ color: 'info.main' }} />;
    case 3:
      if (isRetired) {
        return <BlockIcon fontSize="small" sx={{ color: 'error.main', opacity: 0.85 }} />;
      } else if (isUnfinished) {
        return <RemoveCircleOutlineIcon fontSize="small" sx={{ color: 'warning.main', opacity: 0.5 }} />;
      }
      return <PauseCircleOutlineIcon fontSize="small" sx={{ color: 'warning.main' }} />;
    default: return null;
  }
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
  
  if (diffInDays === 0) return '今日';
  if (diffInDays === 1) return '昨日';
  if (diffInDays < 31) return `${diffInDays}日前`;
  if (diffInDays < 365) return `${Math.floor(diffInDays / 30)}ヶ月前`;
  return `${Math.floor(diffInDays / 365)}年前`;
};


// スコア表示の関数（ReviewItemと同じ）
const getScoreColor = (score: number | null) => {
  if (score === null) return "#666666";
  if (score >= 4.5) return "#4CAF50";
  if (score >= 4.0) return "#8BC34A";
  if (score >= 3.5) return "#CDDC39";
  if (score >= 3.0) return "#FFEB3B";
  if (score >= 2.5) return "#FFC107";
  if (score >= 2.0) return "#FF9800";
  if (score >= 1.5) return "#FF5722";
  return "#F44336";
};

// サブステータスアイコンの表示（GameCardから）
const renderSubStatusIcons = (subStatus: number[], status: number) => {
  if (!subStatus || subStatus.length === 0) return null;

  const icons = [];

  // エンドレス
  if (subStatus.includes(0)) {
    icons.push(
      <Box
        key="endless"
        sx={{
          background: 'linear-gradient(45deg, #37474fc4, #455a64a4)',
          borderRadius: '4px',
          padding: '1px 3px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mr: 0.3,
        }}
      >
        <AllInclusiveIcon sx={{ color: '#e0e0e0', fontSize: '0.7rem' }} />
      </Box>
    );
  }

  // 再プレイ
  if (subStatus.includes(1)) {
    icons.push(
      <Box
        key="replay"
        sx={{
          background: 'linear-gradient(45deg, #f0f0f0d8, #bebebed2)',
          borderRadius: '4px',
          padding: '1px 3px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mr: 0.3,
        }}
      >
        <RefreshIcon sx={{ color: '#383838', fontSize: '0.7rem' }} />
      </Box>
    );
  }

  // コンプリート
  if (status === 1 && subStatus.includes(7)) {
    icons.push(
      <Box
        key="complete"
        sx={{
          background: 'linear-gradient(45deg, #2088ffc4, #a455ffa4)',
          borderRadius: '4px',
          padding: '1px 3px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mr: 0.3,
        }}
      >
        <EmojiEventsIcon sx={{ color: 'white', fontSize: '0.7rem' }} />
      </Box>
    );
  }

  return icons.length > 0 ? (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.3 }}>
      {icons}
    </Box>
  ) : null;
};

const RecentGamesWidget: React.FC<RecentGamesWidgetProps> = ({ username, isPublic = false }) => {
  const [recentGames, setRecentGames] = useState<RecentGame[]>([]);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

  useEffect(() => {
    const fetchRecentGames = async () => {
      try {
        const response = await getRecentPlayedGames(username);
        setRecentGames(response.data);
      } catch (error) {
        console.error('最近のゲーム取得に失敗:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRecentGames();
  }, [username]);

  if (loading) {
    return (
      <Paper elevation={0} sx={{ ...commonPaperStyle, p: { xs: 2, sm: 2.5 } }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
          最近プレイしたゲーム
        </Typography>
        <Grid container spacing={{ xs: 1, sm: 1.5 }}>
          {[...Array(isMdUp ? 10 : 8)].map((_, index) => (
            <Grid item xs={3} sm={3} md={2.4} key={index}>
              <Box sx={{ position: 'relative', paddingTop: '133%' }}>
                <Skeleton 
                  variant="rectangular" 
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    borderRadius: 1
                  }}
                />
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>
    );
  }

  if (recentGames.length === 0) {
    return (
      <Paper elevation={0} sx={{ ...commonPaperStyle, p: { xs: 2, sm: 2.5 } }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
          最近プレイしたゲーム
        </Typography>
        <Box 
          sx={{ 
            p: 3, 
            textAlign: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.02)',
            borderRadius: 1
          }}
        >
          <Typography variant="body2" color="text.secondary">
            最近プレイしたゲームがありません
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={{ ...commonPaperStyle, p: { xs: 1.5, sm: 2.5 } }}>
      <Typography variant="subtitle2" color="text.secondary" lineHeight={1} sx={{ mb: 2 }}>
        最近プレイしたゲーム
      </Typography>
      <Grid container spacing={{ xs: 1, sm: 1.5 }}>
        {recentGames.slice(0, isMdUp ? 10 : 8).map((game) => (
          <Grid item xs={3} sm={3} md={2.4} key={game.game_id}>
            <Box>
              <Link
                to={`/game/${game.game_id}`}
                style={{ textDecoration: 'none' }}
              >
                <Card
                  sx={{
                    position: 'relative',
                    paddingTop: '133%',
                    backgroundColor: 'rgba(255, 255, 255, 0.02)',
                    borderRadius: 1,
                    transition: 'transform 0.2s ease',
                    '&:hover': {
                      transform: 'scale(1.02)'
                    }
                  }}
                >
                  <CardMedia
                    component="img"
                    image={getCoverUrl(game.cover_url)}
                    alt={game.japanese_name || game.name}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: 1
                    }}
                  />

                  {/* サブステータスアイコン */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 6,
                      left: 6,
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    {renderSubStatusIcons(game.sub_status, game.status)}
                  </Box>
                </Card>
              </Link>

              {/* 情報行: アイコン・評価（デスクトップ）、アイコンのみ（モバイル） */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mt: 0.5
                }}
              >
                {/* ステータスアイコンと日付（デスクトップのみ） */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {getStatusIcon(game.status, game.sub_status)}
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.65rem',
                      display: { xs: 'none', sm: 'block' },
                      lineHeight: 1
                    }}
                  >
                    {formatDate(game.priority_date)}
                  </Typography>
                </Box>

                {/* スコア */}
                <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 'auto' }}>
                  {game.score ? (
                    <Typography
                      sx={{
                        fontSize: { xs: "0.65rem", sm: "0.7rem" },
                        py: 0.25,
                        px: 0.75,
                        lineHeight: "1",
                        borderRadius: 1,
                        backgroundColor: `${getScoreColor(game.score)}B3`,
                        color: 'white',
                        fontWeight: 500,
                        minWidth: '28px',
                        textAlign: 'center'
                      }}
                    >
                      {game.score.toFixed(1)}
                    </Typography>
                  ) : (
                    <Box
                      sx={{
                        width: '30px',
                        height: { xs: '17px', sm: '18px' },
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Box
                        sx={{
                          width: '10px',
                          height: '1px',
                          backgroundColor: 'rgba(255, 255, 255, 0.5)'
                        }}
                      />
                    </Box>
                  )}
                </Box>
              </Box>

              {/* 日付（モバイルのみ） */}
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.6rem',
                  display: { xs: 'block', sm: 'none' },
                  mt: 0.3,
                  textAlign: 'right'
                }}
              >
                {formatDate(game.priority_date)}
              </Typography>

              {/* プレイ時間 */}
              {game.play_time != null && game.play_time > 0 && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'pulse.info',
                    fontSize: { xs: '0.55rem', sm: '0.6rem' },
                    mt: { xs: 0, sm: 0.5 },
                    lineHeight: 1,
                    display: 'block',
                    textAlign: { xs: 'right', sm: 'left' },
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {`${game.play_time}時間プレイ`}
                </Typography>
              )}
            </Box>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
};

export default RecentGamesWidget;